import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';

const AlertManagementView: React.FC = () => {
  const [currentMessage, setCurrentMessage] = useState<string | null>(null);
  const [draftMessage, setDraftMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    adminAPI.getActiveAlert()
      .then((data) => {
        if (data?.alert?.message) {
          setCurrentMessage(data.alert.message);
          setDraftMessage(data.alert.message);
        }
      })
      .catch(() => setFeedback({ type: 'error', text: 'Could not load current alert.' }));
  }, []);

  const handleActivate = async () => {
    if (!draftMessage.trim()) return;
    setSaving(true);
    setFeedback(null);
    try {
      await adminAPI.setAlert(draftMessage.trim());
      setCurrentMessage(draftMessage.trim());
      setFeedback({ type: 'success', text: 'Alert activated successfully.' });
    } catch {
      setFeedback({ type: 'error', text: 'Failed to activate alert.' });
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      await adminAPI.clearAlert();
      setCurrentMessage(null);
      setDraftMessage('');
      setFeedback({ type: 'success', text: 'Alert cleared.' });
    } catch {
      setFeedback({ type: 'error', text: 'Failed to clear alert.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="alert-management-view" style={{ padding: '20px', maxWidth: 700 }}>
      <h3 style={{ marginTop: 0, marginBottom: 8 }}>Dealer Alert Banner</h3>
      <p style={{ color: '#666', marginBottom: 16, fontSize: '0.9rem' }}>
        This message appears as a red banner on all dealer dashboards. Leave blank to show no alert.
      </p>

      {currentMessage ? (
        <div style={{
          background: '#DA291C',
          color: '#fff',
          padding: '10px 16px',
          borderRadius: 4,
          marginBottom: 16,
          fontSize: '0.9rem',
          fontWeight: 600
        }}>
          Active: {currentMessage}
        </div>
      ) : (
        <div style={{ background: '#f5f5f5', padding: '10px 16px', borderRadius: 4, marginBottom: 16, fontSize: '0.9rem', color: '#888' }}>
          No active alert
        </div>
      )}

      <textarea
        value={draftMessage}
        onChange={(e) => setDraftMessage(e.target.value)}
        placeholder="Enter alert message for dealers..."
        rows={3}
        style={{
          width: '100%',
          boxSizing: 'border-box',
          padding: '10px',
          fontSize: '0.95rem',
          border: '1px solid #ccc',
          borderRadius: 4,
          resize: 'vertical',
          marginBottom: 12
        }}
      />

      <div style={{ display: 'flex', gap: 10 }}>
        <button
          onClick={handleActivate}
          disabled={saving || !draftMessage.trim()}
          style={{
            background: '#DA291C',
            color: '#fff',
            border: 'none',
            borderRadius: 4,
            padding: '8px 18px',
            fontWeight: 600,
            cursor: saving || !draftMessage.trim() ? 'not-allowed' : 'pointer',
            opacity: saving || !draftMessage.trim() ? 0.6 : 1
          }}
        >
          {saving ? 'Saving...' : 'Activate Alert'}
        </button>

        {currentMessage && (
          <button
            onClick={handleClear}
            disabled={saving}
            style={{
              background: '#fff',
              color: '#333',
              border: '1px solid #ccc',
              borderRadius: 4,
              padding: '8px 18px',
              fontWeight: 600,
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.6 : 1
            }}
          >
            Clear Alert
          </button>
        )}
      </div>

      {feedback && (
        <div style={{
          marginTop: 12,
          padding: '8px 14px',
          borderRadius: 4,
          fontSize: '0.9rem',
          background: feedback.type === 'success' ? '#e8f5e9' : '#fdecea',
          color: feedback.type === 'success' ? '#2e7d32' : '#c62828'
        }}>
          {feedback.text}
        </div>
      )}
    </div>
  );
};

export default AlertManagementView;
